import React, { useCallback, useEffect, useRef } from "react";
import { DataGrid, GridEventListener } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { getVisibleColumns } from "./recordDataTable.config";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setRecordList,
  setSelectedRecord,
  setSelectedEvaluation,
  setEvaluationList,
} from "../../../store/slices/recordSlice";
import { RecordDataType } from "../../../interface";
import { getSessionData } from "../../../utils/helper";
import throttle from "lodash/throttle";

interface RecordDataTableProps {
  rows?: RecordDataType[];
  isLoading?: boolean;
  totalRecord?: number;
  limit: number[];
  updateLimit?: React.Dispatch<React.SetStateAction<number[]>>;
}

const RecordsDataTable: React.FC<RecordDataTableProps> = ({
  rows = [],
  isLoading = false,
  totalRecord = 0,
  updateLimit,
  limit,
}) => {
  const { selectedRecordId } = useSelector((state: RootState) => state.record);
  const dispatch = useDispatch<AppDispatch>();

  const visibleColumns = React.useMemo(
    () => getVisibleColumns(getSessionData()),
    [],
  );

  const fetchMoreData = useCallback(() => {
    if (rows.length < totalRecord) {
      const [startLmit, endLimit] = limit;
      if (startLmit + 100 < totalRecord) {
        updateLimit?.([startLmit + 100, endLimit + 100]);
      }
    }
  }, [rows, totalRecord, limit, updateLimit]);
  const lastScrollTopRef = useRef(0);
  const lastScrollLeftRef = useRef(0);

  useEffect(() => {
    let container: HTMLElement | null = null;
    let rafId: number | null = null;

    const handleScroll = throttle(
      (event: Event) => {
        const target = event.target as HTMLElement;
        const currentTop = target.scrollTop;
        const currentLeft = target.scrollLeft;

        if (currentLeft !== lastScrollLeftRef.current) {
          lastScrollLeftRef.current = currentLeft;
          return;
        }

        if (currentTop <= lastScrollTopRef.current) {
          lastScrollTopRef.current = currentTop;
          return;
        }

        lastScrollTopRef.current = currentTop;

        const distanceToBottom =
          target.scrollHeight - target.clientHeight - target.scrollTop;

        if (distanceToBottom <= 50) {
          fetchMoreData();
        }
      },
      500,
      { leading: false, trailing: true },
    );

    const attach = () => {
      container =
        document.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller") ||
        document.querySelector<HTMLElement>(
          ".MuiDataGrid-virtualScrollerContent",
        );

      if (!container) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      container.addEventListener("scroll", handleScroll, { passive: true });
    };

    attach();

    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
      handleScroll.cancel?.();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [fetchMoreData]);

  const openRecordDetails: GridEventListener<"rowDoubleClick"> = (params) => {
    dispatch(setSelectedRecord(params.row.recordId));
    dispatch(setRecordList(rows.map((i: RecordDataType) => i.recordId)));
    dispatch(setSelectedEvaluation(params.row.evaluation));
    dispatch(setEvaluationList(rows.map((i: RecordDataType) => i.evaluation)));
  };

  return (
    <Box className="grid-container">
      <DataGrid
        loading={isLoading}
        rows={rows.map((item, index) => ({ idn: index + 1, ...item }))}
        columns={visibleColumns}
        disableRowSelectionOnClick
        disableColumnMenu
        rowBufferPx={500}
        columnBufferPx={150}
        onRowDoubleClick={openRecordDetails}
        paginationMode="server"
        rowCount={totalRecord}
        localeText={{
          noRowsLabel: "No records found",
        }}
        getRowClassName={(params) =>
          params?.row?.recordId === selectedRecordId ? "selected-row" : ""
        }
        getRowId={(row) => row?.recordId}
        hideFooter
      />
    </Box>
  );
};

export default RecordsDataTable;
